import { ComponentPropsWithoutRef, forwardRef } from "react";
import styles from "./Drawer.module.scss";
import cx from "classnames";

/**
 * Props for the DrawerContent component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type DrawerContentProps = ComponentPropsWithoutRef<"div">;

/**
 * Content container for the drawer that holds the main content area.
 *
 * Features:
 * - Flexible content area with proper spacing
 * - Scrollable content when needed
 * - Proper semantic HTML structure
 * - Accessible with proper ARIA attributes
 * - Consistent styling with the design system
 * - Supports all standard div element props
 * - Responsive design that adapts to content
 * - Proper focus management
 *
 * @example
 * <Drawer.Content>
 *   <p>This is the main content of the drawer.</p>
 *   <p>It can contain any type of content.</p>
 * </Drawer.Content>
 */
export const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  (props, ref) => {
    const { className, children, ...rest } = props;
    const drawerContentClasses = cx(styles.content, className);

    return (
      <div
        className={drawerContentClasses}
        data-anv="drawer-content"
        ref={ref}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

DrawerContent.displayName = "DrawerContent";
